import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { environment } from '../../environments/environment';

/** Live notification payload (snake_case, mirrors the backend NotificationDTO). */
export interface AppNotification {
  id: number;
  content: string;
  read: boolean;
  user_id: number;
  created_at: string;
  updated_at: string;
  delivered_at: string;
  read_at: string;
  title: string;
  kind: string;
  mission_id: number;
  search_link: string;
}

/**
 * Delivers notifications in real time over the existing STOMP broker (same transport as chat).
 * The backend tells us which topic to listen on via /notifications/subscription, so there is no
 * id-mapping guesswork here. REST remains the source of truth on load; this only streams deltas.
 */
@Injectable({ providedIn: 'root' })
export class NotificationRealtimeService {
  private stompClient: Client | null = null;
  private connected = false;
  private destination: string | null = null;

  private notificationSubject = new Subject<AppNotification>();
  public notifications$: Observable<AppNotification> = this.notificationSubject.asObservable();

  constructor(private http: HttpClient) {}

  /** Look up this user's topic, then open the socket and subscribe. Idempotent. */
  connect(): void {
    if (this.connected || this.stompClient) {
      return;
    }
    this.http
      .get<{ destination: string }>(`${environment.api.BASE_URL}/notifications/subscription`)
      .subscribe({
        next: (info) => {
          if (!info?.destination) {
            return;
          }
          this.destination = info.destination;
          this.openSocket();
        },
        error: (err) => console.error('Notification subscription lookup failed', err),
      });
  }

  private openSocket(): void {
    this.stompClient = new Client({
      webSocketFactory: () => new SockJS(`${environment.api.BASE_URL}/ws`),
      debug: () => {},
      reconnectDelay: 5000,
      onConnect: () => {
        this.connected = true;
        if (this.destination) {
          this.stompClient!.subscribe(this.destination, (message) => {
            try {
              const notif = JSON.parse(message.body) as AppNotification;
              this.playSound();
              this.notificationSubject.next(notif);
            } catch (e) {
              console.error('Malformed notification frame', e);
            }
          });
        }
      },
      onStompError: (frame) => {
        console.error('Notification WebSocket error:', frame);
        this.connected = false;
      },
    });
    this.stompClient.activate();
  }

  disconnect(): void {
    if (this.stompClient) {
      this.stompClient.deactivate();
      this.stompClient = null;
      this.connected = false;
      this.destination = null;
    }
  }

  /** Short beep on a new notification (same Web Audio approach as the chat sound). */
  private playSound(): void {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) {
        return;
      }
      const audioContext = new AudioCtx();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (e) {
      // Autoplay policy may block audio until the user interacts — ignore silently.
    }
  }
}
